import { computed, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';

export function useSeoHead() {
    const { t, locale } = useI18n();
    const route = useRoute();

    // Reactive locale derived from route params
    const currentLocale = computed(() => {
        const routeLocale = route.params.locale as string;
        return (routeLocale === 'en' || routeLocale === 'lt') ? routeLocale : 'en';
    });

    // Sync i18n locale with route
    watchEffect(() => {
        locale.value = currentLocale.value;
    });

    // Base URL from environment variable with fallback
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://reguladata.lt';

    // All locale-dependent values as computed
    const currentPath = computed(() => `/${currentLocale.value}`);
    const canonicalUrl = computed(() => `${baseUrl}${currentPath.value}`);
    const ogImageUrl = computed(() => `${baseUrl}/og-image-${currentLocale.value}.png`);
    const ogLocale = computed(() => currentLocale.value === 'lt' ? 'lt_LT' : 'en_US');
    const ogLocaleAlternate = computed(() => currentLocale.value === 'lt' ? 'en_US' : 'lt_LT');

    // Structured data as computed
    const softwareApplicationSchema = computed(() => JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Regula Data',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'EUR',
            lowPrice: '39',
            highPrice: '129',
        },
        description: t('seo.description'),
        url: baseUrl,
    }));

    const organizationSchema = computed(() => JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Regula Data',
        url: baseUrl,
        logo: `${baseUrl}/favicon.svg`,
        description: t('seo.description'),
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Customer Support',
            email: 'info@reguladata.lt',
        },
    }));

    useHead({
        htmlAttrs: {
            lang: currentLocale,
        },
        title: computed(() => t('seo.title')),
        meta: [
            {
                name: 'description',
                content: computed(() => t('seo.description')),
            },
            {
                name: 'keywords',
                content: computed(() => t('seo.keywords')),
            },
            {
                property: 'og:type',
                content: 'website',
            },
            {
                property: 'og:site_name',
                content: 'Regula Data',
            },
            {
                property: 'og:title',
                content: computed(() => t('seo.ogTitle')),
            },
            {
                property: 'og:description',
                content: computed(() => t('seo.ogDescription')),
            },
            {
                property: 'og:url',
                content: canonicalUrl,
            },
            {
                property: 'og:image',
                content: ogImageUrl,
            },
            {
                property: 'og:image:width',
                content: '1200',
            },
            {
                property: 'og:image:height',
                content: '630',
            },
            {
                property: 'og:locale',
                content: ogLocale,
            },
            {
                property: 'og:locale:alternate',
                content: ogLocaleAlternate,
            },
            {
                name: 'twitter:card',
                content: 'summary_large_image',
            },
            {
                name: 'twitter:title',
                content: computed(() => t('seo.ogTitle')),
            },
            {
                name: 'twitter:description',
                content: computed(() => t('seo.ogDescription')),
            },
            {
                name: 'twitter:image',
                content: ogImageUrl,
            },
        ],
        link: [
            {
                rel: 'canonical',
                href: canonicalUrl,
            },
            {
                rel: 'alternate',
                hreflang: 'en',
                href: `${baseUrl}/en`,
            },
            {
                rel: 'alternate',
                hreflang: 'lt',
                href: `${baseUrl}/lt`,
            },
            {
                rel: 'alternate',
                hreflang: 'x-default',
                href: `${baseUrl}/en`,
            },
        ],
        script: [
            {
                type: 'application/ld+json',
                innerHTML: softwareApplicationSchema,
            },
            {
                type: 'application/ld+json',
                innerHTML: organizationSchema,
            },
        ],
    });
}
